import { RotateCcw } from 'lucide-react';
import { C } from '../constants';
import Glass from '../components/Glass';

export default function UndoScreen({ stateData, district, party, undoLeft, onCancel }) {
  const { partyById, districts, localFont, strings, accent } = stateData;
  const p      = partyById[party];
  const pct    = (5 - undoLeft) / 5;
  const ringR  = 38;
  const ringC  = 2 * Math.PI * ringR;
  const ringDash = ringC * pct;

  if (!p) return null;

  return (
    <main className="px-5 md:px-10 py-10 md:py-14 max-w-3xl mx-auto">
      <div style={{ fontSize: '0.7rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: accent, fontWeight: 600 }}>
        Step 02 · Confirm
      </div>
      <h2 style={{
        fontWeight: 700, fontSize: 'clamp(1.7rem, 4.5vw, 2.6rem)',
        lineHeight: 1.1, letterSpacing: '-0.02em',
        marginTop: '0.5rem', color: C.white,
      }}>
        Locking your vote in{' '}
        <span style={{ color: p.color, textShadow: `0 0 16px ${p.color}66` }}>
          {undoLeft.toFixed(1)}s
        </span>
        …
      </h2>
      <p style={{ fontFamily: localFont, fontSize: '0.95rem', color: C.textDim, marginTop: '0.4rem' }}>
        {strings.undoNote}
      </p>

      {/* Receipt card */}
      <Glass style={{
        marginTop: '2rem', padding: '1.6rem',
        border: `2px solid ${p.color}`,
        boxShadow: `0 0 50px ${p.color}40`,
      }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div style={{ fontSize: '0.62rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: C.textDim, fontWeight: 600 }}>
              {strings.districtLabel}
            </div>
            <div style={{ fontWeight: 700, fontSize: '1.4rem', marginTop: 5, color: C.white, letterSpacing: '-0.01em' }}>
              {district}{' '}
              <span style={{ fontFamily: localFont, fontWeight: 500, fontSize: '0.95rem', color: C.textDim }}>
                · {districts.find(d => d.en === district)?.local}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div style={{ fontSize: '0.62rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: C.textDim, fontWeight: 600 }}>
              {strings.partyLabel}
            </div>
            <div className="flex items-center gap-2 justify-end mt-1.5">
              <span style={{ width: 16, height: 16, backgroundColor: p.color, borderRadius: 3, boxShadow: `0 0 10px ${p.color}`, display: 'inline-block' }} />
              <span style={{ fontWeight: 700, fontSize: '1.4rem', color: C.white, letterSpacing: '-0.01em' }}>{p.en}</span>
            </div>
            <div style={{ fontFamily: localFont, fontSize: '0.82rem', color: C.textDim, marginTop: 2 }}>
              {p.local}
            </div>
          </div>
        </div>

        {/* Circular timer */}
        <div className="mt-6 flex items-center gap-5 flex-wrap">
          <svg width={92} height={92} viewBox="0 0 92 92" style={{ flexShrink: 0 }}>
            <circle cx={46} cy={46} r={ringR}
                    fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={6} />
            <circle cx={46} cy={46} r={ringR}
                    fill="none" stroke={p.color} strokeWidth={6}
                    strokeLinecap="round"
                    strokeDasharray={`${ringDash} ${ringC}`}
                    transform="rotate(-90 46 46)"
                    style={{ filter: `drop-shadow(0 0 8px ${p.color})`, transition: 'stroke-dasharray 50ms linear' }} />
            <text x={46} y={52} textAnchor="middle" fontSize="22" fontWeight="700" fill={C.white}
                  style={{ fontFamily: "'Poppins', sans-serif" }}>
              {Math.ceil(undoLeft)}
            </text>
          </svg>
          <div className="flex-1 min-w-0">
            <div style={{ fontSize: '0.85rem', color: C.textDim, marginBottom: 6 }}>
              Auto-confirms when timer hits zero. Tap Undo to cancel.
            </div>
            <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{
                height: '100%', width: `${pct * 100}%`,
                background: p.color, transition: 'width .05s linear',
                boxShadow: `0 0 8px ${p.color}`,
              }} />
            </div>
          </div>
        </div>
      </Glass>

      <div className="mt-6 flex items-center gap-4 flex-wrap">
        <button
          onClick={onCancel}
          className="inline-flex items-center gap-2"
          style={{
            background:           C.cardBg,
            color:                C.white,
            padding:              '0.9rem 1.4rem',
            fontWeight:           700,
            fontSize:             '1rem',
            border:               `2px solid ${C.borderStrong}`,
            borderRadius:         14,
            cursor:               'pointer',
            backdropFilter:       'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            transition:           'border-color 200ms ease, box-shadow 200ms ease',
            fontFamily:           "'Poppins', sans-serif",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = C.pink; e.currentTarget.style.boxShadow = '0 0 20px rgba(255,77,158,0.3)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = C.borderStrong; e.currentTarget.style.boxShadow = 'none'; }}
        >
          <RotateCcw size={16} />
          Undo · <span style={{ fontFamily: localFont }}>{strings.undoCancel}</span>
        </button>
      </div>
    </main>
  );
}
